import React, { useEffect, useState } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import Button from '@material-ui/core/Button';
import Chip from '@material-ui/core/Chip';
import Container from '@material-ui/core/Container';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import Typography from '@material-ui/core/Typography';

import ActionCognitivePrompt from './ActionCognitivePrompt';
import CockpitNavigation from './CockpitNavigation';
import {
  ID_ACTION_BRANCHE_DEPUIS_CONVERSATION,
  ID_ACTION_TRAVAILLER_FRONTIERE,
  chargerActionsBranche,
  chargerGrapheCognitif,
  construireContexteReprise,
  construirePromptActionCognitive,
  construirePromptTravaillerFrontiere,
  creerActionChangementEtatBranche,
  libelleStatutAction,
  libelleValeurCognitive,
  lireProjectionGrapheCognitifEnCache,
  prochainEtatBasculable,
  trouverActionCognitive,
  trouverBrancheVivante,
} from './grapheCognitifService';

const useStyles = makeStyles((theme) => ({
  page: { paddingTop: theme.spacing(3), paddingBottom: theme.spacing(4), textAlign: 'left' },
  paper: { padding: theme.spacing(2), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #e5e7eb' },
  hero: { padding: theme.spacing(2.5), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #e5e7eb', borderLeft: '4px solid #111827' },
  frontiere: { padding: theme.spacing(2), marginBottom: theme.spacing(2), boxShadow: 'none', border: '1px solid #c7d2fe', borderLeft: '4px solid #4f46e5', backgroundColor: '#f8fafc' },
  frontiereSecondaire: { padding: theme.spacing(1.5), marginTop: theme.spacing(1.25), border: '1px solid #e5e7eb', backgroundColor: '#ffffff' },
  frontiereCible: { padding: theme.spacing(1.5), marginTop: theme.spacing(1.25), border: '2px solid #2563eb', backgroundColor: '#eff6ff' },
  header: { display: 'flex', justifyContent: 'space-between', gap: theme.spacing(2), flexWrap: 'wrap', alignItems: 'flex-start' },
  titre: { maxWidth: 920, lineHeight: 1.15 },
  resume: { maxWidth: 920, marginTop: theme.spacing(1.5), color: '#111827', fontSize: 15, lineHeight: 1.55 },
  muted: { color: '#6b7280', fontSize: 12 },
  actions: { display: 'flex', gap: theme.spacing(1), flexWrap: 'wrap' },
  metaLigne: { display: 'flex', gap: theme.spacing(0.75), flexWrap: 'wrap', marginTop: theme.spacing(1) },
  chipGris: { backgroundColor: '#f3f4f6', color: '#374151', fontSize: 11 },
  chipBleu: { backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: 11 },
  chipLien: { backgroundColor: '#f5f3ff', color: '#5b21b6', fontSize: 11 },
  tag: { backgroundColor: '#f9fafb', color: '#4b5563', fontSize: 11, border: '1px solid #e5e7eb' },
  sectionTitre: { marginBottom: theme.spacing(1) },
  repriseGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: theme.spacing(1.25), marginTop: theme.spacing(1) },
  repriseBloc: { borderLeft: '3px solid #d1d5db', padding: theme.spacing(1), backgroundColor: '#f9fafb' },
  label: { color: '#6b7280', fontSize: 12, fontWeight: 600, marginBottom: 2 },
  valeur: { color: '#111827', fontSize: 14 },
  listeMemoire: { marginTop: 6, paddingLeft: 18, color: '#111827' },
  blocMemoire: { marginTop: theme.spacing(1.5) },
  champFrontiere: { marginTop: theme.spacing(1.25) },
  referencesListe: { marginTop: 6, paddingLeft: 18 },
  contexte: { width: '100%', minHeight: 260, border: '1px solid #d1d5db', borderRadius: 6, padding: theme.spacing(1.25), fontFamily: 'monospace', fontSize: 12, color: '#111827', backgroundColor: '#f9fafb', boxSizing: 'border-box' },
  discret: { border: '1px dashed #d1d5db', backgroundColor: '#fafafa' },
  erreur: { borderLeft: '4px solid #f59e0b', padding: theme.spacing(1.5), backgroundColor: '#fff7ed', marginTop: theme.spacing(1.5) },
}));

const CockpitBrancheVivante = () => {
  const classes = useStyles();
  const { brancheId } = useParams();
  const location = useLocation();
  const frontiereCibleId = new URLSearchParams(location.search).get('frontiere') || '';
  const projectionInitiale = lireProjectionGrapheCognitifEnCache();
  const [projection, setProjection] = useState(projectionInitiale);
  const [chargement, setChargement] = useState(!projectionInitiale);
  const [erreur, setErreur] = useState('');
  const [copie, setCopie] = useState('');
  const [actionOuverte, setActionOuverte] = useState(false);
  const [frontiereOuverte, setFrontiereOuverte] = useState(null);
  const [actionsHumaines, setActionsHumaines] = useState([]);
  const [actionEtatMessage, setActionEtatMessage] = useState('');
  const [actionEtatEnvoi, setActionEtatEnvoi] = useState(false);

  useEffect(() => {
    const projectionEnCache = lireProjectionGrapheCognitifEnCache();
    if (projectionEnCache) {
      setProjection(projectionEnCache);
      setErreur('');
      setChargement(false);
    } else {
      setChargement(true);
    }
    setFrontiereOuverte(null);
    chargerGrapheCognitif()
      .then((payload) => {
        setProjection(payload);
        setErreur('');
        setChargement(false);
      })
      .catch((err) => {
        setErreur(err.message);
        setChargement(false);
      });
  }, [brancheId]);

  useEffect(() => {
    setActionsHumaines([]);
    chargerActionsBranche(brancheId)
      .then((actions) => {
        setActionsHumaines(actions);
      })
      .catch(() => {
        setActionsHumaines([]);
      });
  }, [brancheId]);

  const branche = trouverBrancheVivante(projection, brancheId);

  useEffect(() => {
    if (!branche || !frontiereCibleId) return;
    const index = (branche.frontieres_candidates || []).findIndex((frontiere) => frontiere.id === frontiereCibleId);
    if (index >= 0) setFrontiereOuverte(index);
  }, [branche, frontiereCibleId]);
  const contexte = construireContexteReprise(branche);
  const actionBrancheDepuisConversation = trouverActionCognitive(projection, ID_ACTION_BRANCHE_DEPUIS_CONVERSATION);
  const actionTravaillerFrontiere = trouverActionCognitive(projection, ID_ACTION_TRAVAILLER_FRONTIERE);
  const promptBrancheDepuisConversation = construirePromptActionCognitive(actionBrancheDepuisConversation, {
    contexteBranche: contexte,
  });

  const demanderTransitionEtat = () => {
    const prochainEtat = prochainEtatBasculable(branche);
    if (!prochainEtat) return;
    setActionEtatEnvoi(true);
    creerActionChangementEtatBranche(branche, prochainEtat)
      .then((action) => {
        setActionsHumaines((actions) => [action].concat(actions.filter((item) => item.action_id !== action.action_id)));
        setActionEtatMessage('Demande enregistree. Elle attend la prochaine passe locale SyncStats System.');
        setActionEtatEnvoi(false);
      })
      .catch((err) => {
        setActionEtatMessage(err.message);
        setActionEtatEnvoi(false);
      });
  };

  const copierContexte = () => copierTexte(contexte)
    .then(() => setCopie('Contexte copie.'))
    .catch(() => setCopie('Copie automatique impossible. Le texte peut etre selectionne ci-dessous.'));

  return (
    <Container maxWidth={false} className={classes.page}>
      <CockpitNavigation vueActive="graphe-cognitif" />
      <div className={classes.actions} style={{ marginBottom: 12 }}>
        <Button component={Link} to="/cockpit/graphe-cognitif" variant="outlined" size="small">
          Retour au graphe
        </Button>
        <ActionCognitivePrompt
          prompt={promptBrancheDepuisConversation}
          ouvert={actionOuverte}
          setOuvert={setActionOuverte}
        />
      </div>

      {chargement && (
        <Paper className={classes.paper}>
          <Typography variant="body2" className={classes.muted}>Chargement de la branche...</Typography>
        </Paper>
      )}

      {erreur && (
        <Paper className={classes.paper}>
          <div className={classes.erreur}>
            <Typography variant="subtitle2">Projection indisponible</Typography>
            <Typography variant="body2">{erreur}</Typography>
          </div>
        </Paper>
      )}

      {!chargement && !erreur && !branche && (
        <Paper className={classes.paper}>
          <Typography variant="h5">Branche introuvable</Typography>
          <Typography variant="body2" className={classes.muted}>
            Aucun noeud de Branche vivante ne correspond a cet identifiant stable dans la projection actuelle.
          </Typography>
        </Paper>
      )}

      {branche && (
        <React.Fragment>
          <EnteteBranche branche={branche} classes={classes} />
          <SectionActionEtat
            branche={branche}
            actionsHumaines={actionsHumaines}
            actionEtatMessage={actionEtatMessage}
            actionEtatEnvoi={actionEtatEnvoi}
            demanderTransitionEtat={demanderTransitionEtat}
            classes={classes}
          />
          <SectionFrontieres
            branche={branche}
            actionTravaillerFrontiere={actionTravaillerFrontiere}
            frontiereOuverte={frontiereOuverte}
            setFrontiereOuverte={setFrontiereOuverte}
            classes={classes}
            frontiereCibleId={frontiereCibleId}
          />
          <Grid container spacing={2}>
            <Grid item xs={12} md={7}>
              <SectionMemoire branche={branche} classes={classes} />
            </Grid>
            <Grid item xs={12} md={5}>
              <SectionReprise branche={branche} classes={classes} />
              <SectionContexte
                contexte={contexte}
                copie={copie}
                copierContexte={copierContexte}
                classes={classes}
              />
            </Grid>
          </Grid>
        </React.Fragment>
      )}
    </Container>
  );
};

const EnteteBranche = ({ branche, classes }) => (
  <Paper className={classes.hero}>
    <div className={classes.header}>
      <div>
        <Typography variant="h4" className={classes.titre}>{branche.titre}</Typography>
        <div className={classes.metaLigne}>
          <Chip size="small" label={`Etat: ${libelleValeurCognitive(branche.etat_general)}`} className={classes.chipGris} />
          <Chip size="small" label={`Creee: ${branche.date_creation || 'Inconnue'}`} className={classes.chipGris} />
          {branche.estTransverse && <Chip size="small" label="Branche transverse" className={classes.chipLien} />}
        </div>
        {branche.resume_reconnaissance && (
          <Typography variant="body1" className={classes.resume}>{branche.resume_reconnaissance}</Typography>
        )}
      </div>
    </div>
    {branche.domainesLiesObjets.length > 0 && (
      <ListePuces
        classes={classes}
        titre="Domaines touches"
        items={branche.domainesLiesObjets}
        itemLabel={(domaine) => domaine.titre}
        vide="Aucun autre domaine touche."
        chipClassName={classes.chipLien}
      />
    )}
    <ListePuces
      classes={classes}
      titre="Tags"
      items={branche.tags}
      itemLabel={(tag) => tag}
      vide="Aucun tag."
      chipClassName={classes.tag}
    />
  </Paper>
);

const SectionActionEtat = ({
  branche,
  actionsHumaines,
  actionEtatMessage,
  actionEtatEnvoi,
  demanderTransitionEtat,
  classes,
}) => {
  const prochainEtat = prochainEtatBasculable(branche);
  const actionsRecentes = (actionsHumaines || []).slice(0, 3);
  return (
    <Paper className={classes.paper}>
      <div className={classes.header}>
        <div>
          <Typography variant="h5" className={classes.sectionTitre}>Decision humaine</Typography>
          <Typography variant="body2" className={classes.muted}>
            La demande est conservee dans Monitoring. SyncStats System la validera lors de la prochaine passe locale.
          </Typography>
        </div>
        {prochainEtat && (
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={demanderTransitionEtat}
            disabled={actionEtatEnvoi}
          >
            {actionEtatEnvoi ? 'Enregistrement...' : `Demander: ${libelleValeurCognitive(prochainEtat)}`}
          </Button>
        )}
      </div>
      {!prochainEtat && (
        <Typography variant="body2" className={classes.muted}>
          Aucun basculement simple n'est disponible pour l'etat courant.
        </Typography>
      )}
      {actionEtatMessage && (
        <Typography variant="body2" className={classes.valeur} style={{ marginTop: 10 }}>
          {actionEtatMessage}
        </Typography>
      )}
      {actionsRecentes.length > 0 && (
        <div className={classes.metaLigne}>
          {actionsRecentes.map((action) => (
            <Chip
              key={action.action_id}
              size="small"
              label={`${libelleValeurCognitive(action.valeur_demandee)} - ${libelleStatutAction(action.statut)}`}
              className={classes.chipGris}
            />
          ))}
        </div>
      )}
    </Paper>
  );
};
const SectionMemoire = ({ branche, classes }) => {
  const memoire = branche.memoire_reprise || {};
  if (memoireVide(memoire) && listeVide(branche.references_utiles)) return null;

  return (
    <Paper className={classes.paper}>
      <Typography variant="h5" className={classes.sectionTitre}>Memoire de reprise</Typography>
      <ChampMemoire classes={classes} titre="Pourquoi cette branche existe" valeur={memoire.raison_existence} />
      <ChampMemoire classes={classes} titre="Contexte actuel de reprise" valeur={memoire.contexte_utile} />
      <ChampMemoire classes={classes} titre="Etat actuel connu" valeur={memoire.etat_actuel} />
      <ListeMemoire classes={classes} titre="Decisions acquises" items={memoire.decisions_acquises} />
      <ListeMemoire classes={classes} titre="Preuves connues" items={memoire.preuves_connues} />
      <ListeMemoire classes={classes} titre="Faits etablis" items={memoire.faits_etablis} />
      <ListeMemoire classes={classes} titre="Questions ouvertes" items={memoire.questions_ouvertes} />
      <ListeMemoire classes={classes} titre="Incertitudes a preserver" items={memoire.elements_incertains} />
      <ListeMemoire classes={classes} titre="Artefacts importants" items={memoire.artefacts_mentionnes} />
      <ListeReferences classes={classes} references={branche.references_utiles} />
    </Paper>
  );
};

const SectionReprise = ({ branche, classes }) => (
  <Paper className={classes.paper}>
    <Typography variant="h5" className={classes.sectionTitre}>Indicateurs de reprise</Typography>
    <Typography variant="body2" className={classes.muted}>
      Ces indicateurs decrivent l'effort pour reprendre et la solidite du connu. Ils ne sont pas une priorite ni une urgence.
    </Typography>
    <div className={classes.repriseGrid}>
      <BlocReprise classes={classes} label="Prochaine preuve" valeur={branche.prochaine_preuve || 'Inconnue'} />
      <BlocReprise classes={classes} label="Cout de reprise" valeur={libelleValeurCognitive(branche.energie_activation)} />
      <BlocReprise classes={classes} label="Solidite des preuves" valeur={libelleValeurCognitive(branche.solidite_preuves)} />
      <BlocReprise classes={classes} label="Chaleur derivee" valeur={libelleSignal(branche.chaleur_experimentale.niveau)} />
      <BlocReprise
        classes={classes}
        label="Derniere activite"
        valeur={branche.recence_experimentale.derniere_activite_substantielle || 'Inconnue'}
      />
      <BlocReprise
        classes={classes}
        label="Chemins ouverts"
        valeur={`${branche.chemins_attention.nb_frontieres_actives} Frontiere(s), ${branche.chemins_attention.nb_mouvements_candidats} mouvement(s)`}
      />
    </div>
  </Paper>
);

const SectionFrontieres = ({
  branche,
  actionTravaillerFrontiere,
  frontiereOuverte,
  setFrontiereOuverte,
  classes,
  frontiereCibleId,
}) => {
  const frontieres = branche.frontieres_candidates || [];
  return (
    <Paper className={frontieres.length > 0 ? classes.frontiere : `${classes.paper} ${classes.discret}`}>
      <Typography variant="h5" className={classes.sectionTitre}>Ou pousser maintenant</Typography>
      {frontieres.length === 0 && (
        <Typography variant="body2" className={classes.muted}>
          Aucune Frontiere candidate structuree n'est disponible pour cette branche.
        </Typography>
      )}
      {frontieres.map((frontiere, index) => (
        <div key={frontiere.id || `${frontiere.objectif_question}-${index}`} className={frontiere.id === frontiereCibleId ? classes.frontiereCible : (index === 0 ? '' : classes.frontiereSecondaire)}>
          {frontiere.id === frontiereCibleId && <Chip size="small" label="Frontiere recommandee" className={classes.chipBleu} />}
          {index > 0 && <Typography variant="subtitle2" className={classes.muted}>Frontiere candidate suivante</Typography>}
          <ChampFrontiere classes={classes} titre="Identifiant" valeur={frontiere.id} />
          <ChampFrontiere classes={classes} titre="Objectif / question" valeur={frontiere.objectif_question} />
          <ChampFrontiere classes={classes} titre="Etat connu" valeur={frontiere.etat_connu} />
          <ChampFrontiere classes={classes} titre="Avancee candidate" valeur={frontiere.avancee_candidate} />
          <ChampFrontiere
            classes={classes}
            titre="Type de mouvement experimental"
            valeur={frontiere.type_mouvement_experimental === 'inconnu' ? '' : frontiere.type_mouvement_experimental}
          />
          <ChampFrontiere
            classes={classes}
            titre="Acteur potentiel experimental"
            valeur={frontiere.acteur_potentiel_experimental === 'inconnu' ? '' : frontiere.acteur_potentiel_experimental}
          />
          <ChampFrontiere classes={classes} titre="Preuve attendue" valeur={frontiere.preuve_attendue} />
          <ChampFrontiere classes={classes} titre="Blocage / question" valeur={frontiere.blocage_question} />
          <ChampFrontiere classes={classes} titre="Energie d'activation" valeur={frontiere.energie_activation} />
          <div className={classes.actions} style={{ marginTop: 12 }}>
            <ActionCognitivePrompt
              boutonLibelle="Travailler cette frontiere"
              titre="Travailler cette frontiere"
              description="Copiez ce texte dans ChatGPT pour obtenir une progression raisonnee sur cette Frontiere, sans appel automatique ni mutation du graphe."
              libelleCopie="Copier le texte"
              messageCopieSucces="Texte copie."
              prompt={construirePromptTravaillerFrontiere(actionTravaillerFrontiere, branche, frontiere)}
              ouvert={frontiereOuverte === index}
              setOuvert={(ouvert) => setFrontiereOuverte(ouvert ? index : null)}
            />
          </div>
        </div>
      ))}
    </Paper>
  );
};

const SectionContexte = ({ contexte, copie, copierContexte, classes }) => (
  <Paper className={classes.paper}>
    <div className={classes.header}>
      <div>
        <Typography variant="h5">Contexte de reprise</Typography>
        <Typography variant="body2" className={classes.muted}>
          Export deterministe des informations connues, pret a coller dans une nouvelle conversation.
        </Typography>
      </div>
      <Button variant="contained" color="primary" size="small" onClick={copierContexte}>
        Copier le contexte de reprise
      </Button>
    </div>
    {copie && <Typography variant="body2" className={classes.muted} style={{ marginTop: 8 }}>{copie}</Typography>}
    <textarea className={classes.contexte} readOnly value={contexte} style={{ marginTop: 12 }} />
  </Paper>
);

const BlocReprise = ({ classes, label, valeur }) => (
  <div className={classes.repriseBloc}>
    <div className={classes.label}>{label}</div>
    <div className={classes.valeur}>{valeur}</div>
  </div>
);

const ChampMemoire = ({ classes, titre, valeur }) => {
  if (!valeur) return null;
  return (
    <div className={classes.blocMemoire}>
      <Typography variant="body2" className={classes.label}>{titre}</Typography>
      <Typography variant="body2" className={classes.valeur}>{valeur}</Typography>
    </div>
  );
};

const ChampFrontiere = ({ classes, titre, valeur }) => {
  if (!valeur) return null;
  return (
    <div className={classes.champFrontiere}>
      <Typography variant="body2" className={classes.label}>{titre}</Typography>
      <Typography variant="body2" className={classes.valeur}>{valeur}</Typography>
    </div>
  );
};

const ListeMemoire = ({ classes, titre, items }) => {
  if (!Array.isArray(items) || items.length === 0) return null;
  return (
    <div className={classes.blocMemoire}>
      <Typography variant="body2" className={classes.label}>{titre}</Typography>
      <ul className={classes.listeMemoire}>
        {items.map((item) => <li key={item}><Typography variant="body2">{item}</Typography></li>)}
      </ul>
    </div>
  );
};

const ListeReferences = ({ classes, references }) => {
  if (!Array.isArray(references) || references.length === 0) return null;
  return (
    <div className={classes.blocMemoire}>
      <Typography variant="body2" className={classes.label}>References utiles</Typography>
      <ul className={classes.referencesListe}>
        {references.map((reference) => (
          <li key={`${reference.titre}-${reference.cible}`}>
            <Typography variant="body2">
              {reference.titre}{reference.type ? ` (${reference.type})` : ''}: {reference.cible}
            </Typography>
          </li>
        ))}
      </ul>
    </div>
  );
};

const ListePuces = ({ classes, titre, items, itemLabel, vide, chipClassName }) => (
  <div style={{ marginTop: 14 }}>
    <Typography variant="body2" className={classes.label}>{titre}</Typography>
    <div className={classes.metaLigne}>
      {items.length > 0
        ? items.map((item) => (
          <Chip key={typeof item === 'string' ? item : item.id} size="small" label={itemLabel(item)} className={chipClassName} />
        ))
        : <Typography variant="body2" className={classes.muted}>{vide}</Typography>}
    </div>
  </div>
);

function memoireVide(memoire) {
  return !memoire.raison_existence
    && !memoire.contexte_utile
    && !memoire.etat_actuel
    && listeVide(memoire.faits_etablis)
    && listeVide(memoire.decisions_acquises)
    && listeVide(memoire.preuves_connues)
    && listeVide(memoire.questions_ouvertes)
    && listeVide(memoire.elements_incertains)
    && listeVide(memoire.artefacts_mentionnes);
}

function listeVide(items) {
  return !Array.isArray(items) || items.length === 0;
}

function libelleSignal(valeur) {
  const libelles = {
    chaud: 'Chaud',
    tiede: 'Tiede',
    froid: 'Froid',
    inconnu: 'Inconnu',
  };
  return libelles[valeur] || libelleValeurCognitive(valeur);
}

function copierTexte(texte) {
  if (navigator.clipboard && navigator.clipboard.writeText) {
    return navigator.clipboard.writeText(texte);
  }

  return new Promise((resolve, reject) => {
    try {
      const zone = document.createElement('textarea');
      zone.value = texte;
      zone.setAttribute('readonly', '');
      zone.style.position = 'fixed';
      zone.style.left = '-9999px';
      document.body.appendChild(zone);
      zone.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(zone);
      ok ? resolve() : reject(new Error('copy_failed'));
    } catch (err) {
      reject(err);
    }
  });
}

export default CockpitBrancheVivante;



// WEBPACK FOOTER //
// src/components/cockpit/CockpitBrancheVivante.js